import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import { SparklineComponent } from '../sparkline/sparkline.component';

let kpiCardCounter = 0;

@Component({
  selector: 'app-kpi-card',
  imports: [MatIcon, SparklineComponent],
  templateUrl: './kpi-card.component.html',
  styleUrl: './kpi-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiCardComponent {
  label = input.required<string>();
  value = input.required<string | number>();
  suffix = input<string>('');
  suffixAria = input<string>('');
  icon = input.required<string>();
  iconColorVar = input.required<string>();
  sparkline = input<number[]>([]);

  private readonly translate = inject(TranslateService);

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.currentLang,
  });

  private readonly instanceId = kpiCardCounter++;
  protected readonly labelId = `to-kpi-label-${this.instanceId}`;
  protected readonly valueId = `to-kpi-value-${this.instanceId}`;
  protected readonly labelledBy = `${this.labelId} ${this.valueId}`;

  protected readonly hasSparkline = computed(() => this.sparkline().length > 0);

  protected readonly sparklineAria = computed(() => {
    const data = this.sparkline();
    if (data.length < 2) return '';
    const formatter = new Intl.NumberFormat(this.currentLang() || 'de', { maximumFractionDigits: 1 });
    return this.translate.instant('liveDashboard.sparkline.trendAria', {
      first: formatter.format(data[0]),
      last: formatter.format(data[data.length - 1]),
      min: formatter.format(Math.min(...data)),
      max: formatter.format(Math.max(...data)),
    });
  });

  protected readonly iconColor = computed(() => `var(${this.iconColorVar()})`);

  protected readonly iconBackground = computed(
    () => `color-mix(in srgb, var(${this.iconColorVar()}) 15%, transparent)`,
  );
}
